(function () {
  const groups = new Map();
  let rafId = null;
  let io = null;
  const RESTART_DELAY = 1500;

  const normType = (v) => String(v || 'countdown').trim().toLowerCase();
  const normDivider = (v) => {
    const d = String(v ?? '').trim().toLowerCase();
    return !d || d === 'none' ? '' : String(v).trim();
  };

  const buildMap = (ms, parts) => {
    let rem = ms;
    const map = {};
    const has = (p) => parts.includes(p);

    if (has('DD')) { map.DD = Math.floor(rem / 86400000); rem %= 86400000; }
    if (has('HH')) {
      map.HH = Math.floor(rem / 3600000);
      rem %= 3600000;
      if (has('DD')) map.HH %= 24;
    }
    if (has('MM')) { map.MM = Math.floor(rem / 60000); rem %= 60000; }
    if (has('SS')) { map.SS = Math.floor(rem / 1000); rem %= 1000; }
    if (has('MS')) map.MS = Math.floor(rem / 10);

    return map;
  };

  const pad = (part, val) => (part === 'DD' ? String(val) : String(val).padStart(2, '0'));

  const renderKey = (parts, ms) => {
    const map = buildMap(ms, parts);
    return parts.map((part) => pad(part, map[part] ?? 0)).join('|');
  };

  const render = (el, ms) => {
    const parts = (el.dataset.format || 'HH:MM:SS').split(':');
    const map = buildMap(ms, parts);
    const divider = normDivider(el.dataset.divider);
    const units = [...el.querySelectorAll('.timer-display__unit')];

    if (units.length >= parts.length) {
      parts.forEach((part, i) => {
        const valueEl = units[i]?.querySelector('.timer-display__value') || units[i]?.querySelector('span');
        if (valueEl && map[part] != null) valueEl.textContent = pad(part, map[part]);
      });
      return;
    }

    const spans = [...el.querySelectorAll(':scope > span')];

    if (spans.length >= parts.length) {
      parts.forEach((part, i) => {
        if (spans[i] && map[part] != null) spans[i].textContent = pad(part, map[part]);
      });
      return;
    }

    el.textContent = parts.map((part) => pad(part, map[part] ?? 0)).join(divider);
  };

  const renderZero = (group) => {
    const key = renderKey(group.parts, 0);
    if (group.lastKey === key) return;
    group.lastKey = key;
    group.els.forEach((el) => render(el, 0));
  };

  const endsAt = (type, date, seconds) => {
    if (type === 'date') return new Date(`${date}T00:00:00Z`).getTime();
    return Date.now() + seconds * 1000;
  };

  const remaining = (group) => Math.max(0, group.endsAt - Date.now());

  const clearRestart = (group) => {
    if (group.restartTimeout) {
      clearTimeout(group.restartTimeout);
      group.restartTimeout = null;
    }
    group.restarting = false;
  };

  const scheduleRestart = (group) => {
    clearRestart(group);
    group.restarting = true;
    group.restartTimeout = setTimeout(() => {
      group.restartTimeout = null;
      group.restarting = false;
      group.endsAt = Date.now() + group.duration * 1000;
      group.lastKey = null;
      syncLoop();
    }, RESTART_DELAY);
  };

  const finishGroup = (group) => {
    clearRestart(group);
    group.ended = true;
    if (group.type === 'date') {
      group.els.forEach((el) => { el.textContent = 'Sale Ended'; });
      return;
    }
    renderZero(group);
  };

  const handleExpired = (group) => {
    if (group.restarting || group.ended) return;

    if (group.autoRestart && group.type === 'countdown' && group.duration > 0) {
      renderZero(group);
      scheduleRestart(group);
      return;
    }

    finishGroup(group);
  };

  const syncVisibility = (group) => {
    group.visible = [...group.els].some((el) => el._timerVisible !== false);
    if (group.visible) group.lastKey = null;
    syncLoop();
  };

  const getIO = () => {
    if (!io && 'IntersectionObserver' in window) {
      io = new IntersectionObserver((entries) => {
        const touched = new Set();
        entries.forEach(({ target, isIntersecting }) => {
          const group = target._timerGroup;
          if (!group) return;
          target._timerVisible = isIntersecting;
          touched.add(group);
        });
        touched.forEach(syncVisibility);
      }, { threshold: 0 });
    }
    return io;
  };

  const cancelLoop = () => {
    if (rafId == null) return;
    cancelAnimationFrame(rafId);
    rafId = null;
  };

  const scheduleLoop = () => {
    if (rafId != null || document.hidden) return;
    rafId = requestAnimationFrame(loop);
  };

  const syncLoop = () => {
    const active = [...groups.values()].some((g) => !g.ended && !g.restarting && g.visible);
    if (active && !document.hidden) scheduleLoop();
    else cancelLoop();
  };

  const loop = () => {
    rafId = null;
    if (document.hidden) return;

    let continueLoop = false;

    for (const group of groups.values()) {
      if (group.ended || group.restarting || !group.visible) continue;

      const ms = remaining(group);
      if (ms <= 0) {
        handleExpired(group);
        continue;
      }

      const key = renderKey(group.parts, ms);
      if (key !== group.lastKey) {
        group.lastKey = key;
        group.els.forEach((el) => render(el, ms));
      }

      continueLoop = true;
    }

    if (continueLoop) scheduleLoop();
  };

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      cancelLoop();
      return;
    }
    groups.forEach((g) => { if (g.visible) g.lastKey = null; });
    syncLoop();
  });

  window.initTimerEl = function (el) {
    if (!el || el.dataset.timerReady) return;

    const ref = el.dataset.ref || `timer_${location.pathname}`;
    const type = normType(el.dataset.type);
    const format = el.dataset.format || 'HH:MM:SS';
    const parts = format.split(':');
    const duration = parseInt(el.dataset.time, 10) || 0;
    const autoRestart = el.dataset.autoRestart === 'true';
    el.dataset.timerReady = 'true';

    if (!groups.has(ref)) {
      groups.set(ref, {
        type,
        parts,
        duration,
        autoRestart,
        endsAt: endsAt(type, el.dataset.date, duration),
        els: new Set(),
        visible: true,
        ended: false,
        restarting: false,
        restartTimeout: null,
        lastKey: null,
      });
    }

    const group = groups.get(ref);
    el._timerVisible = true;
    el._timerGroup = group;
    group.els.add(el);
    getIO()?.observe(el);
    syncVisibility(group);

    if (group.ended || group.restarting) {
      if (group.restarting || group.type === 'countdown') renderZero(group);
      return;
    }

    const ms = remaining(group);
    group.lastKey = renderKey(group.parts, ms);
    render(el, ms);
  };

  window.initTimerAll = function () {
    document.querySelectorAll('.timer-display:not([data-timer-ready])').forEach(window.initTimerEl);
  };
})();
